import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateEMI, generateEMISchedule, formatCurrency } from '../utils/calculations';
import { generateEMIPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const emiFaqs = [
  {
    question: 'How is EMI calculated?',
    answer: 'EMI is calculated using the loan amount, interest rate, and total number of months. paisapyara now supports reducing balance, flat rate, and floating rate loan modes.',
  },
  {
    question: 'Does a longer loan tenure reduce EMI?',
    answer: 'Yes. A longer tenure usually lowers your monthly EMI, but it also increases the total interest paid over the life of the loan.',
  },
  {
    question: 'Can I use this EMI calculator for home, car, and personal loans?',
    answer: 'Yes. You can use the same EMI calculator for most fixed-rate loan comparisons by changing the amount, rate, and tenure.',
  },
  {
    question: 'What is the benefit of the amortization schedule?',
    answer: 'The amortization schedule shows how each EMI is split between principal and interest month by month, along with the remaining balance.',
  },
  {
    question: 'What is a flat rate loan?',
    answer: 'In a flat rate loan, interest is calculated on the full principal for the entire tenure, so the EMI is usually higher than a reducing balance loan at the same nominal rate.',
  },
  {
    question: 'How does floating rate EMI work?',
    answer: 'Floating rate EMI can change when the interest rate resets. This calculator estimates the current EMI and the revised EMI after the reset point you enter.',
  },
];

const EMICalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [calcType, setCalcType] = useState('reducing');
  const [principal, setPrincipal] = useState(1000000);
  const [rate, setRate] = useState(8.5);
  const [tenure, setTenure] = useState(60);
  const [floatingRate, setFloatingRate] = useState(9.5);
  const [resetAfterMonths, setResetAfterMonths] = useState(24);
  const [result, setResult] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, rate, tenure, calcType, floatingRate, resetAfterMonths]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculateFlatEMI = (amount, annualRate, months) => {
    const totalInterest = amount * (annualRate / 100) * (months / 12);
    const totalPayment = amount + totalInterest;
    const emi = months > 0 ? totalPayment / months : 0;

    return {
      emi,
      totalPayment,
      totalInterest,
    };
  };

  const generateFlatSchedule = (amount, annualRate, months) => {
    const scheduleRows = [];
    const monthlyInterest = amount * (annualRate / 12 / 100);
    const flatEMI = months > 0 ? (amount + amount * (annualRate / 100) * (months / 12)) / months : 0;
    let balance = amount;

    for (let month = 1; month <= months; month += 1) {
      const principalPaid = Math.min(balance, flatEMI - monthlyInterest);
      balance = Math.max(0, balance - principalPaid);

      scheduleRows.push({
        month,
        emi: Math.round(flatEMI),
        principal: Math.round(principalPaid),
        interest: Math.round(monthlyInterest),
        balance: Math.round(balance),
      });
    }

    return scheduleRows;
  };

  const generateFloatingSchedule = (amount, initialRate, revisedRateValue, months, resetMonth) => {
    const scheduleRows = [];
    const initialMonthlyRate = initialRate / 12 / 100;
    const revisedMonthlyRate = revisedRateValue / 12 / 100;
    let balance = amount;

    const initialEMI = initialMonthlyRate === 0 || months <= 0
      ? (months > 0 ? amount / months : 0)
      : amount * initialMonthlyRate * Math.pow(1 + initialMonthlyRate, months) / (Math.pow(1 + initialMonthlyRate, months) - 1);

    const monthsUntilReset = Math.max(0, Math.min(months, resetMonth));
    let revisedEMI = initialEMI;

    for (let month = 1; month <= months; month += 1) {
      if (month === monthsUntilReset + 1 && month <= months) {
        const remainingMonths = months - month + 1;
        revisedEMI = revisedMonthlyRate === 0 || remainingMonths <= 0
          ? (remainingMonths > 0 ? balance / remainingMonths : 0)
          : balance * revisedMonthlyRate * Math.pow(1 + revisedMonthlyRate, remainingMonths) / (Math.pow(1 + revisedMonthlyRate, remainingMonths) - 1);
      }

      const currentMonthlyRate = month <= monthsUntilReset ? initialMonthlyRate : revisedMonthlyRate;
      const emi = month <= monthsUntilReset ? initialEMI : revisedEMI;
      const interest = balance * currentMonthlyRate;
      const principalPaid = Math.min(balance, emi - interest);
      balance = Math.max(0, balance - principalPaid);

      scheduleRows.push({
        month,
        emi: Math.round(emi),
        principal: Math.round(principalPaid),
        interest: Math.round(interest),
        balance: Math.round(balance),
      });
    }

    return { scheduleRows, initialEMI, revisedEMI };
  };

  const calculate = () => {
    const p = Number(principal) || 0;
    const r = Number(rate) || 0;
    const t = Number(tenure) || 0;
    const floatingRateValue = Number(floatingRate) || 0;
    const resetMonthValue = Number(resetAfterMonths) || 0;

    if (calcType === 'flat') {
      const flatResult = calculateFlatEMI(p, r, t);
      setResult({
        ...flatResult,
        type: 'flat',
        isFloating: false,
        isFlat: true,
      });
      setSchedule(generateFlatSchedule(p, r, t));
      return;
    }

    if (calcType === 'floating') {
      const floatingResult = generateFloatingSchedule(p, r, floatingRateValue, t, resetMonthValue);
      const totalPayment = floatingResult.scheduleRows.reduce((sum, row) => sum + row.emi, 0);
      const totalInterest = totalPayment - p;

      setResult({
        emi: Math.round(floatingResult.initialEMI) || 0,
        revisedEmi: Math.round(floatingResult.revisedEMI) || 0,
        totalPayment: Math.round(totalPayment) || 0,
        totalInterest: Math.round(totalInterest) || 0,
        type: 'floating',
        resetAfterMonths: resetMonthValue,
        revisedRate: floatingRateValue,
        isFloating: true,
        isFlat: false,
      });
      setSchedule(floatingResult.scheduleRows);
      return;
    }
    
    const emi = calculateEMI(p, r, t);
    const totalPayment = emi * t;
    const totalInterest = totalPayment - p;
    const scheduleData = generateEMISchedule(p, r, t);

    setResult({
      emi: Math.round(emi) || 0,
      totalPayment: Math.round(totalPayment) || 0,
      totalInterest: Math.round(totalInterest) || 0,
      type: 'reducing',
      isFloating: false,
      isFlat: false,
    });
    setSchedule(scheduleData);
  };

  const handleDownloadPDF = () => {
    generateEMIPDF({
      mode: calcType,
      principal,
      rate,
      floatingRate,
      resetAfterMonths,
      tenure,
      emi: result.emi,
      revisedEmi: result.revisedEmi,
      totalPayment: result.totalPayment,
      totalInterest: result.totalInterest,
      schedule,
    });
  };

  const handleReset = () => {
    setCalcType('reducing');
    setPrincipal(1000000);
    setRate(8.5);
    setTenure(60);
    setFloatingRate(9.5);
    setResetAfterMonths(24);
  };

  const chartData = result ? {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [{
      data: [principal || 0, result.totalInterest],
      backgroundColor: ['#2563eb', '#f59e0b'],
      borderColor: ['#1d4ed8', '#d97706'],
      borderWidth: 2,
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="EMI Calculator"
        description="Free EMI Calculator India - Calculate reducing balance, flat rate, and floating rate EMIs for home loan, car loan, and personal loan. Get instant EMI, total interest & payment schedule with PDF export."
        keywords="EMI calculator, loan EMI calculator, flat rate EMI calculator, floating rate EMI calculator, home loan EMI, car loan EMI, personal loan EMI, EMI calculation formula, monthly installment calculator India"
        canonicalPath="/emi"
        calculatorType="EMI Calculator"
        faqItems={emiFaqs}
      />
      <div className="calculator-header">
        <h1>EMI Calculator</h1>
        <p>Calculate your Equated Monthly Installment for any loan</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Calculation Type</label>
            <div className="tabs">
              <button
                className={`tab ${calcType === 'reducing' ? 'active' : ''}`}
                onClick={() => setCalcType('reducing')}
              >
                Reducing Balance
              </button>
              <button
                className={`tab ${calcType === 'flat' ? 'active' : ''}`}
                onClick={() => setCalcType('flat')}
              >
                Flat Rate
              </button>
              <button
                className={`tab ${calcType === 'floating' ? 'active' : ''}`}
                onClick={() => setCalcType('floating')}
              >
                Floating Rate
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Loan Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="10000"
                max="100000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="10000"
                max="10000000"
                step="10000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
                style={{ '--value': `${((principal || 0) / 10000000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>{calcType === 'floating' ? 'Current Interest Rate (% p.a.)' : 'Interest Rate (% p.a.)'}</label>
            <input
              type="number"
              value={rate === '' ? '' : rate}
              onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="30"
              step="0.1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                step="0.1"
                value={rate || 0}
                onChange={(e) => setRate(Number(e.target.value))}
                style={{ '--value': `${((rate || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          {calcType === 'floating' && (
            <>
              <div className="input-group">
                <label>Expected Revised Rate After Reset (% p.a.)</label>
                <input
                  type="number"
                  value={floatingRate === '' ? '' : floatingRate}
                  onChange={(e) => setFloatingRate(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                  max="30"
                  step="0.1"
                />
              </div>

              <div className="input-group">
                <label>Rate Reset After (Months)</label>
                <input
                  type="number"
                  value={resetAfterMonths === '' ? '' : resetAfterMonths}
                  onChange={(e) => setResetAfterMonths(e.target.value === '' ? '' : Number(e.target.value))}
                  min="1"
                  max={tenure}
                  step="1"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  EMI will be recalculated for the remaining tenure after this month.
                </small>
              </div>
            </>
          )}

          <div className="input-group">
            <label>Loan Tenure (Months)</label>
            <input
              type="number"
              value={tenure === '' ? '' : tenure}
              onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
              min="6"
              max="360"
            />
            <div className="slider-container">
              <input
                type="range"
                min="6"
                max="360"
                step="6"
                value={tenure || 0}
                onChange={(e) => setTenure(Number(e.target.value))}
                style={{ '--value': `${((tenure || 0) / 360) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              {Math.floor((tenure || 0) / 12)} years {(tenure || 0) % 12} months
            </small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>
                  {calcType === 'floating' && result.revisedEmi
                    ? 'Initial EMI'
                    : calcType === 'flat'
                      ? 'Flat Rate EMI'
                      : 'Monthly EMI'}
                </h3>
                <div className="amount">{formatCurrency(result.emi)}</div>
                {calcType === 'floating' && result.revisedEmi && (
                  <small>Revised EMI after reset: {formatCurrency(result.revisedEmi)}</small>
                )}
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Amount</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                {calcType === 'flat' && (
                  <div className="result-item">
                    <span className="label">Flat Interest Charge</span>
                    <span className="value">{formatCurrency(result.totalInterest)}</span>
                  </div>
                )}
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value">{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Payment</span>
                  <span className="value">{formatCurrency(result.totalPayment)}</span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} options={chartOptions} />
                </div>
              )}

              <button 
                className="btn btn-outline" 
                style={{ width: '100%', marginTop: '20px' }}
                onClick={() => setShowSchedule(!showSchedule)}
              >
                {showSchedule ? 'Hide' : 'Show'} {calcType === 'flat' ? 'Flat Rate Schedule' : calcType === 'floating' ? 'Floating Rate Schedule' : 'Amortization Schedule'}
              </button>

              {showSchedule && (
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '16px' }}>
                  <table className="schedule-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>EMI</th>
                        <th>Principal</th>
                        <th>Interest</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map((row) => (
                        <tr key={row.month}>
                          <td>{row.month}</td>
                          <td>{formatCurrency(row.emi)}</td>
                          <td>{formatCurrency(row.principal)}</td>
                          <td>{formatCurrency(row.interest)}</td>
                          <td>{formatCurrency(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About EMI</h4>
        <p>
          EMI (Equated Monthly Installment) is a payment amount made by a borrower to a lender at a specified date each calendar month.
          The calculator supports reducing balance, flat rate, and floating rate loan comparisons.
        </p>
        <p style={{ marginTop: '8px', fontFamily: 'monospace' }}>
          EMI = P × r × (1 + r)^n / ((1 + r)^n - 1)
        </p>
        <p style={{ marginTop: '8px' }}>
          Where P = Principal, r = Monthly interest rate, n = Number of months
        </p>
        <ul style={{ marginTop: '8px' }}>
          <li><strong>Reducing balance:</strong> Interest is charged on the outstanding principal.</li>
          <li><strong>Flat rate:</strong> Interest is charged on the full principal throughout the tenure.</li>
          <li><strong>Floating rate:</strong> EMI can change after the reset point when the rate moves.</li>
        </ul>
      </div>

      <section className="info-box" style={{ marginTop: '24px' }}>
        <h4>EMI Calculator FAQs</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {emiFaqs.map((item) => (
            <details key={item.question} style={{ border: '1px solid #dbe3f0', borderRadius: 12, background: '#fff', padding: '14px 16px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#0f172a' }}>{item.question}</summary>
              <p style={{ margin: '12px 0 0', color: '#475569', lineHeight: 1.65 }}>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default EMICalculator;

